import { MapPin, Eye, ExternalLink, Copy, Check, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Badge } from '../../../../foundation/altzero_base/base/components/ui/badge';
import type { BvSite } from '../../types/billValidationTypes';
import { effectiveSiteStatus } from '../../services/siteService';
import type { SiteListSortField } from '../../services/siteService';
import { useState } from 'react';

const STATUS_STYLES: Record<string, string> = {
  Enrolled: 'bg-green-50 text-green-700 border-green-200',
  'Pending Enrolment': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'Not Enrolled': 'bg-gray-50 text-gray-500 border-gray-200',
  'Pending Cancellation': 'bg-orange-50 text-orange-700 border-orange-200',
  Cancelled: 'bg-red-50 text-red-600 border-red-200',
};

const METER_LABELS: Record<string, string> = {
  mondo: 'Mondo',
  powermetric: 'PM',
};

// Clickable sortable columns (created_at is a valid sort target server-side
// but has no header — it is the default/tiebreaker).
type SortField = Exclude<SiteListSortField, 'created_at'>;

type Props = {
  sites: BvSite[];
  loading: boolean;
  sortField: SiteListSortField | null;
  sortDir: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  onSelect: (site: BvSite) => void;
  onOpenNewWindow: (site: BvSite) => void;
  selectedIds?: Set<string>;
  onToggleOne?: (id: string) => void;
  onTogglePage?: (ids: string[], checked: boolean) => void;
};

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SiteListSortField | null; sortDir: 'asc' | 'desc' }) {
  if (sortField !== field) return <ChevronsUpDown className="h-3 w-3 ml-1 opacity-30 inline" />;
  return sortDir === 'asc'
    ? <ChevronUp className="h-3 w-3 ml-1 text-blue-500 inline" />
    : <ChevronDown className="h-3 w-3 ml-1 text-blue-500 inline" />;
}

export function SiteTable({
  sites, loading, sortField, sortDir, onSort, onSelect, onOpenNewWindow,
  selectedIds, onToggleOne, onTogglePage,
}: Props) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const bulkEnabled = !!(selectedIds && onToggleOne && onTogglePage);
  const pageIds = sites.map((s) => s.id);
  const allPageSelected = bulkEnabled && pageIds.length > 0 && pageIds.every((id) => selectedIds!.has(id));
  const somePageSelected = bulkEnabled && pageIds.some((id) => selectedIds!.has(id));

  const copyNmi = (e: React.MouseEvent, id: string, nmi: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(nmi);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // Server already filters + sorts + paginates; render `sites` as-is.
  const th = (label: string, field: SortField) => (
    <th
      className="px-4 py-3 cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      {label}
      <SortIcon field={field} sortField={sortField} sortDir={sortDir} />
    </th>
  );

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <MapPin className="h-10 w-10 mb-3 opacity-30" />
        <p className="text-sm">No sites found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-xs text-gray-500 font-medium uppercase tracking-wider">
            {bulkEnabled && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded-[4px] border-gray-300 cursor-pointer"
                  checked={allPageSelected}
                  ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                  onChange={(e) => onTogglePage!(pageIds, e.target.checked)}
                  title="Select all on this page"
                />
              </th>
            )}
            <th className="px-4 py-3 w-12">View</th>
            {th('NMI', 'nmi')}
            <th className="px-4 py-3">Address</th>
            {th('Entity', 'org')}
            {th('Retailer', 'retailer')}
            {th('Distributor', 'distributor')}
            {th('Tariff', 'tariff')}
            {th('Meter', 'meter')}
            {th('Status', 'status')}
            <th className="px-4 py-3">Embedded</th>
            {th('Enrolled', 'enrolled')}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sites.map((s) => (
            <tr key={s.id} className="hover:bg-blue-50/40 transition-colors">
              {bulkEnabled && (
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded-[4px] border-gray-300 cursor-pointer"
                    checked={selectedIds!.has(s.id)}
                    onChange={() => onToggleOne!(s.id)}
                  />
                </td>
              )}
              <td className="px-4 py-3">
                <div className="flex items-center gap-0.5">
                  <button
                    // Cmd/Ctrl-click and middle-click open a new tab, matching what
                    // a link does anywhere else. These are buttons rather than
                    // anchors, so the browser gives no such behaviour for free and
                    // the only route to a new tab was the icon beside this one.
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey) { e.preventDefault(); onOpenNewWindow(s); return; }
                      onSelect(s);
                    }}
                    onAuxClick={(e) => {
                      if (e.button === 1) { e.preventDefault(); onOpenNewWindow(s); }
                    }}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                    title="View site — Cmd/Ctrl-click for a new tab"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenNewWindow(s); }}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                    title="Open in new window"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
              <td className="px-4 py-3 font-mono text-xs text-gray-700">
                <span className="inline-flex items-center gap-1 group/nmi">
                  {s.nmi ?? '—'}
                  {s.nmi && (
                    <button
                      onClick={(e) => copyNmi(e, s.id, s.nmi!)}
                      className="opacity-0 group-hover/nmi:opacity-100 transition-opacity text-gray-400 hover:text-gray-700"
                      title="Copy NMI"
                    >
                      {copiedId === s.id
                        ? <Check className="h-3 w-3 text-green-500" />
                        : <Copy className="h-3 w-3" />}
                    </button>
                  )}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-600 text-xs">
                {[s.addr_street, s.addr_city, s.addr_state].filter(Boolean).join(', ') || '—'}
              </td>
              <td className="px-4 py-3 text-gray-700">{s.client?.name ?? '—'}</td>
              <td className="px-4 py-3 text-gray-700 text-xs">{s.active_retailer ?? '—'}</td>
              <td className="px-4 py-3 text-gray-500 text-xs">{s.bv_extn?.network_distributor ?? '—'}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-600">{s.bv_extn?.network_tariff_code ?? '—'}</td>
              <td className="px-4 py-3 text-xs text-gray-500">
                {s.bv_extn?.metering_provider
                  ? (METER_LABELS[s.bv_extn.metering_provider] ?? s.bv_extn.metering_provider)
                  : '—'}
              </td>
              <td className="px-4 py-3">
                {/* Job 28: the list showed the stored literal, so a site whose
                    unenrolment date had passed still read "Enrolled" here while
                    the site page — which reconciles the two — said otherwise.
                    Same reconciliation, same answer on both screens. */}
                {(() => {
                  const { status, stored, note } = effectiveSiteStatus(s.bv_extn);
                  return (
                    <Badge
                      variant="outline"
                      className={`text-xs ${STATUS_STYLES[status] ?? ''}`}
                      title={note ?? undefined}
                    >
                      {status}
                      {note && <span className="ml-1 text-amber-600" title={`Saved as "${stored}"`}>*</span>}
                    </Badge>
                  );
                })()}
              </td>
              <td className="px-4 py-3 text-xs text-gray-500">
                {s.bv_extn?.is_embedded ? 'Yes' : 'No'}
              </td>
              <td className="px-4 py-3 text-xs text-gray-400">
                {s.bv_extn?.enrolment_date
                  ? new Date(s.bv_extn.enrolment_date).toLocaleDateString('en-AU')
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
